/**
 * Tutor help levels in learner words (study-capture-fixes-prd.md F-05, F-14). The server keeps
 * its H0 to H4 rules; the learner sees what each button does, and the notes match what the
 * server allows:
 * - no saved attempt: only "What is it asking?" works;
 * - an assessment: "Show the next step" and "Show a worked example" are off;
 * - a parent turned worked examples off: "Show a worked example" is off.
 */

/** Learner words for the server help levels H0 to H4 (F-05). */
export const HELP_LEVELS = [
  { level: 0, label: 'What is it asking?' },
  { level: 1, label: 'Give me a hint' },
  { level: 2, label: 'How do I start?' },
  { level: 3, label: 'Show the next step' },
  { level: 4, label: 'Show a worked example' },
] as const;

export const HELP_UNLOCK_COPY = 'Write what you tried to unlock more help.';
export const ASSESSMENT_HELP_COPY = 'This is for an assessment, so worked examples are off.';
export const PARENT_WORKED_OFF_COPY = 'Worked examples are off. A parent can change this.';

/** The learner words for a help level, for example "Give me a hint". */
export function helpLevelLabel(level: number): string {
  return HELP_LEVELS.find((item) => item.level === level)?.label ?? HELP_LEVELS[0].label;
}

/** Help levels that need a saved attempt first. */
const ATTEMPT_LEVEL = 1;
/** Help levels the server refuses in an assessment session. */
const ASSESSMENT_BLOCKED_LEVEL = 3;
/** The worked-example level a parent can turn off. */
const WORKED_LEVEL = 4;

export interface HelpLevelPolicy {
  hasAttempt: boolean;
  assessment: boolean;
  /** False when a parent turned worked examples off. */
  workedAllowed: boolean;
  /** The institution or class cap. */
  maxLevel: number;
}

/** True when the server would accept this help level now. */
export function helpLevelAllowed(level: number, policy: HelpLevelPolicy): boolean {
  if (level > policy.maxLevel) return false;
  if (level >= ATTEMPT_LEVEL && !policy.hasAttempt) return false;
  if (policy.assessment && level >= ASSESSMENT_BLOCKED_LEVEL) return false;
  if (level === WORKED_LEVEL && !policy.workedAllowed) return false;
  return true;
}

/** The notes shown under the buttons; none when every shown button works. */
export function helpLevelNotes(policy: HelpLevelPolicy): string[] {
  const notes: string[] = [];
  if (!policy.hasAttempt && policy.maxLevel >= ATTEMPT_LEVEL) notes.push(HELP_UNLOCK_COPY);
  if (policy.assessment && policy.maxLevel >= ASSESSMENT_BLOCKED_LEVEL)
    notes.push(ASSESSMENT_HELP_COPY);
  else if (!policy.workedAllowed && policy.maxLevel >= WORKED_LEVEL)
    notes.push(PARENT_WORKED_OFF_COPY);
  return notes;
}
